import { useMemo, useState } from 'react';

import { ControlledDevice } from '@crossnokaye/typescript-sdk/models/components';
import { cn } from '@crossnokaye/ux-common';

import { SequencerDevice } from '@repo/sdk/models/components';

import { buildEquipmentNavSearch } from '@/components/navigation/left/components/shared/equipment-nav-search';
import { NavLink } from '@/components/navigation/nav-link';
import { useResolvedEquipmentViewId } from '@/features/equipment-browser/hooks/use-resolved-equipment-view-id';
import { Popover, PopoverContent, PopoverTrigger } from '@/ux-common/components/popover/popover';

// Helper to get common properties from either device type
const getDeviceProps = (device: ControlledDevice | SequencerDevice) => {
  if ('deviceId' in device) {
    // ControlledDevice
    return {
      id: device.deviceId,
      displayName: device.name ?? device.alias,
    };
  }
  // SequencerDevice
  return {
    id: device.id,
    displayName: device.displayName,
  };
};

interface SequencerNavProps {
  children?: React.ReactNode;
  onSelect?: () => void;
  routeId: string;
  sequencerDevices: ControlledDevice[] | SequencerDevice[];
}

export const SequencerNav = ({
  children,
  onSelect,
  routeId,
  sequencerDevices,
}: SequencerNavProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const resolvedViewId = useResolvedEquipmentViewId();

  // Sort sequencers by display name
  const sequencers = useMemo(() => {
    if (!sequencerDevices) return [];
    return [...sequencerDevices].sort((a, b) => {
      const propsA = getDeviceProps(a);
      const propsB = getDeviceProps(b);
      return (propsA.displayName ?? '').localeCompare(propsB.displayName ?? '');
    });
  }, [sequencerDevices]);

  const searchParamsById = useMemo(
    () =>
      Object.fromEntries(
        sequencers.map((sequencer) => {
          const { id } = getDeviceProps(sequencer);
          return [
            id,
            buildEquipmentNavSearch({
              routeId,
              viewId: resolvedViewId,
              equipmentId: id,
            }),
          ];
        })
      ),
    [sequencers, routeId, resolvedViewId]
  );

  const handleSequencerClick = () => {
    onSelect?.();
    setIsOpen(false);
  };

  // Shared sequencer list rendering
  const sequencerList = (
    <>
      {sequencers.length === 0 ? (
        <div className="rounded-sm px-2 py-1.5 text-sm text-muted-foreground">
          No sequencers found
        </div>
      ) : (
        sequencers.map((sequencer) => {
          const { id, displayName } = getDeviceProps(sequencer);
          return (
            <NavLink
              key={id}
              routeId={routeId}
              searchParams={searchParamsById[id]}
              onClick={handleSequencerClick}
            >
              <div
                className={cn(
                  'rounded-sm px-2 py-1.5 text-sm font-medium outline-none transition-colors cursor-pointer',
                  'focus:bg-accent hover:bg-accent'
                )}
              >
                {displayName}
              </div>
            </NavLink>
          );
        })
      )}
    </>
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      {/* The registry popover draws no surface, so the list brings its own: the
          look the package popover painted, until design specifies one. */}
      <PopoverContent
        className={'min-w-32 rounded-md bg-popover p-1 text-left text-popover-foreground shadow-md'}
        side={'right'}
      >
        {sequencerList}
      </PopoverContent>
    </Popover>
  );
};
